// src/components/ParamSlider/ParamSlider.tsx

import React, { useId } from 'react';
import styles from './ParamSlider.module.css';

interface ParamSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  helpText?: string;
  onChange: (v: number) => void;
  formatValue?: (v: number) => string;
}

export const ParamSlider: React.FC<ParamSliderProps> = ({
  label,
  value,
  min,
  max,
  step = 1,
  unit = '',
  helpText,
  onChange,
  formatValue,
}) => {
  const id = useId();
  const pct = ((value - min) / (max - min)) * 100;
  const display = formatValue ? formatValue(value) : `${value}${unit}`;

  return (
    <div className={styles.slider}>
      <div className={styles.header}>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
        <span className={styles.value}>{display}</span>
      </div>
      <input
        id={id}
        type="range"
        className={styles.range}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ '--fill': `${pct}%` } as React.CSSProperties}
      />
      <div className={styles.bounds}>
        <span>
          {min}
          {unit}
        </span>
        <span>
          {max}
          {unit}
        </span>
      </div>
      {helpText && <p className={styles.help}>{helpText}</p>}
    </div>
  );
};

/* ── Toggle ── */

interface ParamToggleProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  helpText?: string;
}

export const ParamToggle: React.FC<ParamToggleProps> = ({ label, checked, onChange, helpText }) => {
  const id = useId();
  return (
    <div className={styles.toggleRow}>
      <label htmlFor={id} className={styles.toggleLabel}>
        {label}
      </label>
      <button
        id={id}
        role="switch"
        aria-checked={checked}
        className={`${styles.toggle} ${checked ? styles.toggleOn : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className={styles.toggleThumb} />
      </button>
      {helpText && <p className={styles.help}>{helpText}</p>}
    </div>
  );
};

export default ParamSlider;
